import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BatchLead, BatchLeadFieldsApi, BatchLeadFieldsResult, LeadFieldsKey, LeadFieldsPatch } from './types';
import { INLINE_EDIT_COPY, ROW_KEYS_OF_FIELD, applyPatchToRow, deriveDisplayName } from './inlineEdit';

/**
 * `useLeadRowSync`: the Batch grid's inline-edit sync store (spec 2026-09-17
 * §2.2, viq-admin#55). It is what makes an inline edit "just save": one
 * request at a time per lead, optimistic display, automatic retry on
 * transient failures, and an unsaved value is never silently dropped.
 *
 * Hosted by `BatchScreen`, NOT the table: the screen unmounts the table while
 * the operator is on the CRM Clean / Match / Send tabs, and saves in flight
 * must survive that. The table subscribes and renders
 * `row ⊕ confirmed ⊕ failed ⊕ pending` through `overlay`.
 *
 * Per lead:
 *   - `pending`  — every value applied optimistically and not yet confirmed,
 *                  the newest per field (it includes what is in flight);
 *   - `inFlight` — the snapshot currently being sent, through its retries;
 *   - `failed`   — values whose save gave up, kept with their message until
 *                  the operator retries, edits again or discards them;
 *   - `confirmed`— the server's answer for the fields it saved, shown over the
 *                  row until a list load that STARTED after the answer lands
 *                  (`epoch` / `prune`), so a refetch already in flight when
 *                  the save confirmed can't paint the old value back.
 *
 * Only the fields that were SENT are ever merged into a row (plus the
 * response's `completeness` / `crmSentAt`, and `preview` for a name edit),
 * never a copy of the whole row, so a save can't undo a tag, an assignment or
 * any other change made to the same row meanwhile.
 */

export type LeadSyncWaiting = 'rate_limited' | 'retrying' | null;

export type LeadFieldStatus =
  | { kind: 'idle' }
  | { kind: 'saving'; waiting: LeadSyncWaiting }
  | { kind: 'failed'; message: string };

/** The latest thing worth announcing (the grid's polite live region). */
export interface LeadSyncNotice {
  seq: number;
  kind: 'failed' | 'rate_limited';
  leadId: string;
  fields: LeadFieldsKey[];
  message: string;
}

export interface LeadRowSyncStore {
  /** Apply `patch` optimistically and queue it. `conferenceId` is the event the
   *  edit was made under (its KPI tiles are refreshed after the save). */
  enqueue: (leadId: string, patch: LeadFieldsPatch, meta?: { conferenceId?: string | null }) => void;
  /** Re-queue failed values: all of the lead's, or just `fields`. */
  retry: (leadId: string, fields?: LeadFieldsKey[]) => void;
  /** Drop failed values (all, or just `fields`) and fall back to the server's. */
  discard: (leadId: string, fields?: LeadFieldsKey[]) => void;
  /** The row as the table shows it. Returns `row` itself when nothing applies. */
  overlay: (row: BatchLead) => BatchLead;
  status: (leadId: string, field: LeadFieldsKey) => LeadFieldStatus;
  /** Anything pending, in flight or failed — the page-leave guard's test. */
  hasUnsynced: () => boolean;
  /** A save is waiting out a 429 right now. */
  rateLimited: () => boolean;
  lastNotice: () => LeadSyncNotice | null;
  /** Read before a list load starts; pass the value to `prune` once it lands. */
  epoch: () => number;
  /** Forget confirmed values the load that started at `epoch` already carries. */
  prune: (epoch: number) => void;
  subscribe: (listener: () => void) => () => void;
  /** Changes on every state change (useSyncExternalStore's snapshot). */
  getVersion: () => number;
  /**
   * Hears every confirmed save with the row values it confirmed (what
   * `overlay` shows from it), for a host that keeps rows of its own in step
   * (the unified CRM grid's shared rows), so a later prune can't leave them
   * behind.
   */
  onConfirmed: (listener: (leadId: string, values: Partial<BatchLead>) => void) => () => void;
}

export interface LeadRowSyncOptions {
  getApi: () => BatchLeadFieldsApi | undefined;
  /** After every successful save (the hook's query invalidations). */
  onSaved?: (leadId: string, meta: { conferenceId: string | null }) => void;
  /** Injectable for tests; defaults to setTimeout. */
  setTimer?: (fn: () => void, ms: number) => void;
}

/** Retries after the first try: network, 5xx and 429 only (spec §2.2). */
const MAX_RETRIES = 3;
const BACKOFF_MS = [1_000, 2_000, 4_000];
/** A `Retry-After` beyond this is treated as this. The write bucket refills
 *  a token a second, so the server never asks for more than a minute. */
const MAX_RETRY_AFTER_MS = 60_000;

interface ThrownLike {
  statusCode?: unknown;
  status?: unknown;
  message?: unknown;
  errors?: unknown;
  retryAfter?: unknown;
}

function statusOf(err: unknown): number | undefined {
  const e = err as ThrownLike | null | undefined;
  const s = e?.statusCode ?? e?.status;
  return typeof s === 'number' && s > 0 ? s : undefined;
}

/** How long to wait before the next try, or null when this failure is final. */
function retryDelayMs(err: unknown, retriesSoFar: number): number | null {
  if (retriesSoFar >= MAX_RETRIES) return null;
  const s = statusOf(err);
  const transient = s === undefined || s >= 500 || s === 429;
  if (!transient) return null;
  const ra = (err as ThrownLike | null | undefined)?.retryAfter;
  if (typeof ra === 'number' && Number.isFinite(ra) && ra > 0) {
    return Math.min(ra * 1_000, MAX_RETRY_AFTER_MS);
  }
  return BACKOFF_MS[retriesSoFar];
}

function plainMessage(err: unknown): string | null {
  const m = (err as ThrownLike | null | undefined)?.message;
  return typeof m === 'string' && m.trim() !== '' ? m : null;
}

/** Thrown in place of a request when the host passed no API (cells are never
 *  editable then, so this is a guard, not a path). Not retried. */
const NO_API = Symbol('no lead-fields API');

/** The message a failed field keeps. 400s carry the server's own words (per
 *  field when it names one); everything else is ours, never raw status text. */
function failureMessage(err: unknown, field: LeadFieldsKey): string {
  if (err === NO_API) return INLINE_EDIT_COPY.fallback;
  const s = statusOf(err);
  if (s === 404) return INLINE_EDIT_COPY.deleted;
  if (s === 400) {
    const fieldErrors = (err as ThrownLike | null | undefined)?.errors as
      | Record<string, unknown>
      | undefined;
    const own = fieldErrors?.[field];
    if (Array.isArray(own) && typeof own[0] === 'string' && own[0] !== '') return own[0];
    return plainMessage(err) ?? INLINE_EDIT_COPY.fallback;
  }
  if (s === 429) return INLINE_EDIT_COPY.rateLimitedGaveUp;
  if (s === undefined) return INLINE_EDIT_COPY.networkGaveUp;
  if (s >= 500) return INLINE_EDIT_COPY.serverGaveUp;
  if (s === 403) return INLINE_EDIT_COPY.forbidden;
  return plainMessage(err) ?? INLINE_EDIT_COPY.fallback;
}

interface Entry {
  pending: LeadFieldsPatch;
  inFlight: LeadFieldsPatch | null;
  waiting: LeadSyncWaiting;
  failed: Partial<Record<LeadFieldsKey, { value: string; message: string }>>;
  confirmed: Partial<Record<keyof BatchLead, { value: unknown; epoch: number }>>;
  conferenceId: string | null;
}

const IDLE: LeadFieldStatus = { kind: 'idle' };

function fieldsOf(patch: LeadFieldsPatch): LeadFieldsKey[] {
  return (Object.keys(patch) as LeadFieldsKey[]).filter((k) => patch[k] !== undefined);
}

/** The framework-free store. `useLeadRowSync` wraps it for React. */
export function createLeadRowSyncStore(opts: LeadRowSyncOptions): LeadRowSyncStore {
  const setTimer = opts.setTimer ?? ((fn: () => void, ms: number) => void setTimeout(fn, ms));
  const entries = new Map<string, Entry>();
  const listeners = new Set<() => void>();
  const confirmListeners = new Set<(leadId: string, values: Partial<BatchLead>) => void>();
  let version = 0;
  let epochCounter = 0;
  let noticeSeq = 0;
  let notice: LeadSyncNotice | null = null;

  function changed() {
    version += 1;
    for (const l of Array.from(listeners)) l();
  }

  function entryFor(leadId: string): Entry {
    let e = entries.get(leadId);
    if (!e) {
      e = { pending: {}, inFlight: null, waiting: null, failed: {}, confirmed: {}, conferenceId: null };
      entries.set(leadId, e);
    }
    return e;
  }

  function dropIfEmpty(leadId: string) {
    const e = entries.get(leadId);
    if (
      e &&
      !e.inFlight &&
      fieldsOf(e.pending).length === 0 &&
      Object.keys(e.failed).length === 0 &&
      Object.keys(e.confirmed).length === 0
    ) {
      entries.delete(leadId);
    }
  }

  function announce(kind: LeadSyncNotice['kind'], leadId: string, fields: LeadFieldsKey[], message: string) {
    noticeSeq += 1;
    notice = { seq: noticeSeq, kind, leadId, fields, message };
  }

  /** Send whatever has accumulated, unless a request for this lead is out. */
  function pump(leadId: string) {
    const e = entries.get(leadId);
    if (!e || e.inFlight) return;
    const fields = fieldsOf(e.pending);
    if (fields.length === 0) {
      dropIfEmpty(leadId);
      return;
    }
    const snapshot: LeadFieldsPatch = {};
    for (const f of fields) snapshot[f] = e.pending[f];
    e.inFlight = snapshot;
    void send(leadId, snapshot, 0);
  }

  async function send(leadId: string, snapshot: LeadFieldsPatch, retriesSoFar: number) {
    const api = opts.getApi();
    try {
      if (!api) throw NO_API;
      const { lead } = await api.updateFields(leadId, snapshot);
      succeeded(leadId, snapshot, lead);
    } catch (err) {
      const delay = err === NO_API ? null : retryDelayMs(err, retriesSoFar);
      const e = entryFor(leadId);
      if (delay !== null) {
        e.waiting = statusOf(err) === 429 ? 'rate_limited' : 'retrying';
        if (e.waiting === 'rate_limited') {
          announce('rate_limited', leadId, fieldsOf(snapshot), INLINE_EDIT_COPY.rateLimited);
        }
        changed();
        setTimer(() => {
          e.waiting = null;
          changed();
          void send(leadId, snapshot, retriesSoFar + 1);
        }, delay);
        return;
      }
      failedFinally(leadId, snapshot, err);
    }
  }

  function succeeded(leadId: string, sent: LeadFieldsPatch, lead: BatchLeadFieldsResult | undefined) {
    const e = entryFor(leadId);
    const epoch = ++epochCounter;
    const answer = (lead ?? {}) as Partial<BatchLead>;
    // What the sent fields read as on the row, from the server's answer where
    // it gave one, else from what was sent.
    const sentAsRow = applyPatchToRow(
      {
        id: leadId,
        preview: '',
        createdAt: '',
        captureType: 'voice',
        repOid: '',
      },
      sent,
    );
    const values: Record<string, unknown> = {};
    for (const field of fieldsOf(sent)) {
      for (const key of ROW_KEYS_OF_FIELD[field]) {
        if (key === 'preview') continue;
        const value = key in answer ? answer[key] : sentAsRow[key];
        e.confirmed[key] = { value, epoch };
        values[key] = value;
      }
      // A newer edit to the same field stays pending (and keeps showing).
      if (e.pending[field] === sent[field]) delete e.pending[field];
    }
    if (sent.firstName !== undefined || sent.lastName !== undefined) {
      if (typeof answer.preview === 'string') {
        e.confirmed.preview = { value: answer.preview, epoch };
        values.preview = answer.preview;
      } else {
        // An answer without the derived name (not today's backend): keep
        // showing the name as sent rather than snapping back to the old one.
        const derived = deriveDisplayName(sent.firstName ?? '', sent.lastName ?? '', '');
        if (derived !== '') {
          e.confirmed.preview = { value: derived, epoch };
          values.preview = derived;
        }
      }
    }
    // The red "!" refreshes in place. A null answer means rules are off OR
    // the server's rules lookup failed after the write landed; either way the
    // row's own value is the better one to keep showing.
    if (answer.completeness != null) {
      e.confirmed.completeness = { value: answer.completeness, epoch };
      e.confirmed.crmSentAt = { value: answer.crmSentAt ?? null, epoch };
      values.completeness = answer.completeness;
      values.crmSentAt = answer.crmSentAt ?? null;
    }
    e.inFlight = null;
    e.waiting = null;
    changed();
    for (const l of Array.from(confirmListeners)) l(leadId, values as Partial<BatchLead>);
    opts.onSaved?.(leadId, { conferenceId: e.conferenceId });
    pump(leadId);
  }

  function failedFinally(leadId: string, sent: LeadFieldsPatch, err: unknown) {
    const e = entryFor(leadId);
    const gaveUp: LeadFieldsKey[] = [];
    for (const field of fieldsOf(sent)) {
      // Superseded by a newer edit: that one is still pending and goes next.
      if (e.pending[field] !== sent[field]) continue;
      e.failed[field] = { value: sent[field] as string, message: failureMessage(err, field) };
      delete e.pending[field];
      gaveUp.push(field);
    }
    e.inFlight = null;
    e.waiting = null;
    if (gaveUp.length > 0) announce('failed', leadId, gaveUp, e.failed[gaveUp[0]]!.message);
    changed();
    pump(leadId);
  }

  return {
    enqueue(leadId, patch, meta) {
      const fields = fieldsOf(patch);
      if (fields.length === 0) return;
      const e = entryFor(leadId);
      for (const f of fields) {
        e.pending[f] = patch[f];
        delete e.failed[f];
      }
      if (meta?.conferenceId !== undefined) e.conferenceId = meta.conferenceId;
      changed();
      pump(leadId);
    },

    retry(leadId, fields) {
      const e = entries.get(leadId);
      if (!e) return;
      const which = (fields ?? (Object.keys(e.failed) as LeadFieldsKey[])).filter((f) => e.failed[f]);
      if (which.length === 0) return;
      for (const f of which) {
        e.pending[f] = e.failed[f]!.value;
        delete e.failed[f];
      }
      changed();
      pump(leadId);
    },

    discard(leadId, fields) {
      const e = entries.get(leadId);
      if (!e) return;
      const which = fields ?? (Object.keys(e.failed) as LeadFieldsKey[]);
      let any = false;
      for (const f of which) {
        if (e.failed[f]) {
          delete e.failed[f];
          any = true;
        }
      }
      if (!any) return;
      dropIfEmpty(leadId);
      changed();
    },

    overlay(row) {
      const e = entries.get(row.id);
      if (!e) return row;
      let out: BatchLead = row;
      const confirmedKeys = Object.keys(e.confirmed) as Array<keyof BatchLead>;
      if (confirmedKeys.length > 0) {
        out = { ...out };
        for (const k of confirmedKeys) (out as unknown as Record<string, unknown>)[k] = e.confirmed[k]!.value;
      }
      const unsaved: LeadFieldsPatch = {};
      for (const f of Object.keys(e.failed) as LeadFieldsKey[]) unsaved[f] = e.failed[f]!.value;
      for (const f of fieldsOf(e.pending)) unsaved[f] = e.pending[f];
      if (fieldsOf(unsaved).length > 0) out = applyPatchToRow(out, unsaved);
      return out;
    },

    status(leadId, field) {
      const e = entries.get(leadId);
      if (!e) return IDLE;
      const f = e.failed[field];
      if (f) return { kind: 'failed', message: f.message };
      if (e.pending[field] !== undefined) return { kind: 'saving', waiting: e.waiting };
      return IDLE;
    },

    hasUnsynced() {
      for (const e of entries.values()) {
        if (e.inFlight || fieldsOf(e.pending).length > 0 || Object.keys(e.failed).length > 0) return true;
      }
      return false;
    },

    rateLimited() {
      for (const e of entries.values()) if (e.waiting === 'rate_limited') return true;
      return false;
    },

    lastNotice: () => notice,

    epoch: () => epochCounter,

    prune(epoch) {
      let any = false;
      for (const [leadId, e] of entries) {
        for (const k of Object.keys(e.confirmed) as Array<keyof BatchLead>) {
          if (e.confirmed[k]!.epoch <= epoch) {
            delete e.confirmed[k];
            any = true;
          }
        }
        dropIfEmpty(leadId);
      }
      if (any) changed();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getVersion: () => version,

    onConfirmed(listener) {
      confirmListeners.add(listener);
      return () => {
        confirmListeners.delete(listener);
      };
    },
  };
}

/** Debounce for the after-save refreshes (spec §2.2: 2 s). */
export const SAVED_REFRESH_DEBOUNCE_MS = 2_000;

export interface UseLeadRowSyncOptions {
  /** The shell's inline-edit API. Absent → an inert store nothing is queued on. */
  api?: BatchLeadFieldsApi;
  /**
   * Once per burst of saves, after the debounce, with the events the saves
   * were made under — `BatchScreen` re-runs its CRM check from here.
   */
  onSavedBurst?: (conferenceIds: string[]) => void;
}

/**
 * The store for React: one per host component, stable for its lifetime.
 * Besides the store itself it owns the side effects of a save (spec §2.2):
 *
 *   - `invalidateQueries(['leadDetail', id])` per save, so an open or
 *     later-opened drawer is fresh;
 *   - a debounced (2 s) `invalidateQueries(['batchAggregations', conferenceId])`
 *     for the events edited in, so the tiles catch up after a burst, and the
 *     `onSavedBurst` callback alongside it. The table's list is NOT reloaded:
 *     the response already updated the row, and a refetch would collapse the
 *     loaded pages;
 *   - a `beforeunload` guard while anything is pending, in flight or failed.
 */
export function useLeadRowSync({ api, onSavedBurst }: UseLeadRowSyncOptions = {}): LeadRowSyncStore {
  const queryClient = useQueryClient();
  const apiRef = useRef(api);
  apiRef.current = api;
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const burstRef = useRef(onSavedBurst);
  burstRef.current = onSavedBurst;

  // The debounced refresh, as plain closure state: nothing here renders.
  const [debounced] = useState(() => {
    const conferenceIds = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    function flush() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      const ids = Array.from(conferenceIds);
      conferenceIds.clear();
      for (const id of ids) {
        void queryClientRef.current.invalidateQueries({ queryKey: ['batchAggregations', id] });
      }
      burstRef.current?.(ids);
    }
    return {
      saved(conferenceId: string | null) {
        if (conferenceId) conferenceIds.add(conferenceId);
        if (timer !== null) clearTimeout(timer);
        timer = setTimeout(flush, SAVED_REFRESH_DEBOUNCE_MS);
      },
      /** On unmount: refresh now rather than from a timer nobody owns. */
      flushIfPending() {
        if (timer !== null) flush();
      },
    };
  });

  const [store] = useState(() =>
    createLeadRowSyncStore({
      getApi: () => apiRef.current,
      onSaved: (leadId, { conferenceId }) => {
        void queryClientRef.current.invalidateQueries({ queryKey: ['leadDetail', leadId] });
        debounced.saved(conferenceId);
      },
    }),
  );

  useEffect(() => () => debounced.flushIfPending(), [debounced]);

  // Page-leave guard (the LeadDetailBody pattern), registered only while
  // something is unsynced. Driven by a subscription, not by re-rendering the
  // host on every store change.
  useEffect(() => {
    let registered = false;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const sync = () => {
      const want = store.hasUnsynced();
      if (want && !registered) {
        window.addEventListener('beforeunload', handler);
        registered = true;
      } else if (!want && registered) {
        window.removeEventListener('beforeunload', handler);
        registered = false;
      }
    };
    sync();
    const unsubscribe = store.subscribe(sync);
    return () => {
      unsubscribe();
      if (registered) window.removeEventListener('beforeunload', handler);
    };
  }, [store]);

  return store;
}
